import { zodResolver } from "@hookform/resolvers/zod"
import { useTranslation } from "next-i18next"
import Head from "next/head"
import Link from "next/link"
import { useRouter } from "next/router"
import { SubmitHandler, useForm } from "react-hook-form"
import { Label } from "../../../components/landing/components/Fields"
import { Alert } from "../../../components/ui/Alert"
import { Button } from "../../../components/ui/button"
import { AuthLayout } from "../../../layouts/AuthLayout"
import { signInSchema } from "../../../validation/auth"
import Image from "next/image"
import { useState } from "react"
import { FcGoogle } from "react-icons/fc"
import { motion } from "framer-motion"
import { Loader2 } from "lucide-react"
import useAuth from "../../../hooks/useAuth"

interface SignInFormValues {
  email: string
  password: string
}

export default function SignIn() {
  const router = useRouter()
  const { t } = useTranslation("common")
  const [loading, setLoading] = useState(false)
  const { signInWithEmail, signInWithGoogle } = useAuth()
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError
  } = useForm<SignInFormValues>({
    resolver: zodResolver(signInSchema)
  })

  const onSubmit: SubmitHandler<SignInFormValues> = async (values) => {
    setLoading(true)
    try {
      const { data, error } = await signInWithEmail(
        values.email,
        values.password
      )

      if (error) {
        // @ts-ignore
        setError("submit", {
          type: "manual",
          message: error.message || t("Invalid email or password")
        })
      } else if (data.user) {
        router.push("/onboarding")
      }
    } catch (error) {
      console.error("Sign-in error:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    setLoading(true)
    try {
      const { error } = await signInWithGoogle()
      if (!error) router.push("/onboarding")
    } catch (error) {
      console.error("Google sign-in error:", error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Head>
        <title>{t("Sign In")} - Seedmancer</title>
      </Head>
      <AuthLayout>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col items-center"
        >
          <Link href="/" className="transition-transform hover:scale-105">
            <Image
              src="/logo.png"
              alt="Logo"
              width={120}
              height={120}
              className="w-auto h-16"
              priority
            />
          </Link>
          <div className="mt-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900">
              {t("Welcome back")}
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {t("Don't have an account?")}{" "}
              <Link
                href="/auth/sign-up"
                className="font-medium text-emerald-600 hover:text-emerald-500 transition-colors"
              >
                {t("Sign Up")}
              </Link>
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="w-full max-w-sm mx-auto mt-8"
        >
          <Button
            type="button"
            variant="outline"
            className="w-full flex items-center justify-center gap-2 py-5 transition-all hover:bg-gray-50"
            onClick={handleGoogleSignIn}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <FcGoogle className="w-5 h-5" />
            )}
            <span>{t("Continue with Google")}</span>
          </Button>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center">
              <span className="px-4 text-sm text-gray-500 bg-white">
                {t("or continue with email")}
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* @ts-ignore */}
            {errors.submit && <Alert title={t(errors.submit.message)} />}

            <div className="space-y-1">
              <Label id="email">{t("Email Address")}</Label>
              <input
                className="block w-full rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-gray-900 placeholder-gray-400 transition-colors focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-emerald-500 sm:text-sm"
                type="email"
                placeholder="you@example.com"
                {...register("email")}
              />
              {errors.email && (
                <p className="text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-1">
              <Label id="password">{t("Password")}</Label>
              <input
                className="block w-full rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-gray-900 placeholder-gray-400 transition-colors focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-emerald-500 sm:text-sm"
                type="password"
                placeholder="••••••••"
                {...register("password")}
              />
              {errors.password && (
                <p className="text-sm text-red-600">
                  {errors.password.message}
                </p>
              )}
            </div>

            <div className="flex flex-col space-y-4">
              <Button
                type="submit"
                variant="filled"
                className="w-full py-5 transition-all"
                disabled={isSubmitting || loading}
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  t("Sign In")
                )}
              </Button>

              <Link
                href="/auth/reset-password"
                className="text-sm text-center text-emerald-600 hover:text-emerald-500 transition-colors"
              >
                {t("Forgot your password?")}
              </Link>
            </div>
          </form>
        </motion.div>
      </AuthLayout>
    </>
  )
}
